// Turnkey configurator catalog (TEST page /<region>/turnkey_test).
//
// TWO data sources, deliberately separated:
//
//  1. VRS HARDWARE — never hardcoded here. Only the itemType ALLOWLISTS below
//     live in this file; the name / price / currency / image / availability all
//     come from the live region offers payload (useRegionOffers → the SSR
//     price bundle, the same array that drives the shop grid). So a price change
//     in the backend/IM shows up here with no code change, and an itemType that
//     is not in the region's payload silently drops out of its step.
//
//  2. THIRD-PARTY GEAR (PCs, monitors, TVs, keyboard) — VRS does not sell these,
//     so there is no offer to read. They are listed below with an indicative
//     retail price captured from alternate.de (Aug 2026) in EUR, converted to the
//     page currency with the static FX table. These are ESTIMATES for the
//     configurator total, not quotes — the UI labels them as such.

// --- 1. VRS hardware: itemType allowlists, in the order each step displays ----

// Step 1 — the rig base. All seven GT-2 cockpit colours.
export const RIG_TYPES = [
  'GT2_COCKPIT_BLACK',
  'GT2_COCKPIT_WHITE',
  'GT2_COCKPIT_RED',
  'GT2_COCKPIT_BLUE',
  'GT2_COCKPIT_GREEN',
  'GT2_COCKPIT_ORANGE',
  'GT2_COCKPIT_TURQUOISE',
]

// Step 2 — seat.
export const SEAT_TYPES = ['GT2_SEAT']

// Step 3a — monitor mount / stand (single-select). The last three are the
// FREE-STANDING stands (TX43 / TX60): they do not bolt to the rig, and they are
// what unlocks the large-format screen options in step 9.
export const MOUNT_TYPES = [
  'SX57_SINGLE_MONITOR_MOUNT_100x100',
  'SX57_SINGLE_MONITOR_MOUNT_200x200',
  'TX32_TRIPLE_MONITOR_STAND_100x100',
  'TX32_TRIPLE_MONITOR_STAND_200x200',
  'TX43_TRIPLE_MONITOR_STAND_100x100',
  'TX43_TRIPLE_MONITOR_STAND_200x200',
  'TX60_TV_STAND',
]

// The two free-standing stands get a badge in the mount step.
export const FREE_STANDING_TYPES = new Set([
  'TX43_TRIPLE_MONITOR_STAND_100x100',
  'TX43_TRIPLE_MONITOR_STAND_200x200',
  'TX60_TV_STAND',
])

// Step 3b — rig accessories (multi-select).
export const RIG_ACCESSORY_TYPES = ['SHIFTER_MOUNT', 'KEYBOARD_MOUNT', 'PC_TRAY']

// Step 4a — wheel bases (single-select). The uDFP20 and DFP20 are discontinued, and
// the uDFP20 *_UPGRADE itemTypes are torque unlocks for an owned base, not a base.
export const WHEELBASE_TYPES = ['DFP15_WHEEL_BASE']

// Step 4b — where the base bolts to the rig. Both kits are EUR 0 line items.
export const MOUNT_KIT_TYPES = ['GT2_FRONT_MOUNT_KIT', 'GT2_BOTTOM_MOUNT_KIT']

// Step 5 — pedals (single-select set) + step 5b springs (multi-select).
export const PEDAL_TYPES = ['PEDALS_THROTTLE_AND_BRAKE_CLUTCH_PEDAL', 'PEDALS_THROTTLE_AND_BRAKE']
export const PEDAL_EXTRA_TYPES = ['CLUTCH_PEDAL', 'THROTTLE_SPRING', 'BRAKE_SPRING', 'CLUTCH_SPRING']

// Step 6 — steering wheels (single-select).
export const WHEEL_TYPES = [
  'R295_STEERING_WHEEL_G2',
  'VRS_ASCHER_ULTIMATE_WHEEL_QR_CONNECT_WHEEL_ADAPTER',
  'LITE_FORMULA_STEERING_WHEEL',
]

// Step 7 — quick releases, adapters and brackets (multi-select).
export const ACCESSORY_TYPES = [
  'QR_CONNECT_WHEEL_ADAPTER',
  'R295_ADAPTER',
  'QUICK_RELEASE_BASE_SIDE_80MM',
  'QUICK_RELEASE_WHEEL_SIDE',
  'HUB_ADAPTER_V2',
  'MOUNTING_BRACKET',
]

// --- 2. Screen logic: the mount decides which screens are offered -------------

// Which screen family a mount unlocks. Keyed by itemType so a new mount only
// needs one line here.
export const MOUNT_SCREEN_PROFILE = {
  SX57_SINGLE_MONITOR_MOUNT_100x100: 'single',
  SX57_SINGLE_MONITOR_MOUNT_200x200: 'single',
  TX32_TRIPLE_MONITOR_STAND_100x100: 'triple32',
  TX32_TRIPLE_MONITOR_STAND_200x200: 'triple32',
  TX43_TRIPLE_MONITOR_STAND_100x100: 'triple43',
  TX43_TRIPLE_MONITOR_STAND_200x200: 'triple43',
  TX60_TV_STAND: 'tripleTv',
}

export const SCREEN_PROFILE_COPY = {
  single: {
    title: 'Single ultrawide',
    caption: 'The GT2-SX57 takes one screen, so these are super-ultrawides: 49" for the usual setup, 57" dual-4K if you want the widest single panel we support.',
  },
  triple32: {
    title: 'Triple 27" or 32"',
    caption: 'The GT2-TX32 is rated for three 27" to 32" panels. Prices below are per screen; the total counts three.',
  },
  triple43: {
    title: 'Triple 32" or 43"',
    caption: 'The free-standing TX43 carries three panels up to 43". Prices below are per screen; the total counts three.',
  },
  tripleTv: {
    title: 'Triple TV, 50" to 60"',
    caption: 'The free-standing TX60 is built for TVs from 50" to 60". Prices below are per screen; the total counts three.',
  },
}

// qty = how many of this screen the build needs (1 for the single mount, 3 for
// every triple stand). priceEur = indicative retail, alternate.de, Aug 2026.
export const SCREEN_OPTIONS = {
  single: [
    {
      id: 'samsung-neo-g95nc-57',
      name: 'Samsung Odyssey Neo G95NC (S57CG954NU)',
      spec: '57" 7680x2160 dual-4K · 240 Hz · Mini LED VA · 32:9',
      size: '57"',
      qty: 1,
      priceEur: 1594,
    },
    {
      id: 'samsung-g95c-49',
      name: 'Samsung Odyssey G9 G95C (S49CG954EU)',
      spec: '49" 5120x1440 · 240 Hz · VA · 32:9',
      size: '49"',
      qty: 1,
      priceEur: 739,
    },
    {
      id: 'msi-491cqpde-49',
      name: 'MSI MPG 491CQPDE QD-OLED',
      spec: '49" 5120x1440 · 144 Hz · QD-OLED · 32:9',
      size: '49"',
      qty: 1,
      priceEur: 899,
    },
  ],
  triple32: [
    {
      id: 'asus-vg27aql5a',
      name: 'ASUS TUF Gaming VG27AQL5A',
      spec: '27" 2560x1440 · 210 Hz · IPS',
      size: '27"',
      qty: 3,
      priceEur: 199.9,
    },
    {
      id: 'gigabyte-mo27q2a',
      name: 'GIGABYTE MO27Q2A QD-OLED',
      spec: '27" 2560x1440 · 280 Hz · QD-OLED',
      size: '27"',
      qty: 3,
      priceEur: 410,
    },
    {
      id: 'samsung-g70d-32',
      name: 'Samsung Odyssey G70D (S32DG702EU)',
      spec: '32" 3840x2160 · 144 Hz · IPS',
      size: '32"',
      qty: 3,
      priceEur: 379,
    },
    {
      id: 'gigabyte-mo32u2',
      name: 'GIGABYTE MO32U2 QD-OLED',
      spec: '32" 3840x2160 · 240 Hz · QD-OLED',
      size: '32"',
      qty: 3,
      priceEur: 629,
    },
  ],
  triple43: [
    {
      id: 'samsung-g70d-32-tx43',
      name: 'Samsung Odyssey G70D (S32DG702EU)',
      spec: '32" 3840x2160 · 144 Hz · IPS',
      size: '32"',
      qty: 3,
      priceEur: 379,
    },
    {
      id: 'gigabyte-mo32u2-tx43',
      name: 'GIGABYTE MO32U2 QD-OLED',
      spec: '32" 3840x2160 · 240 Hz · QD-OLED',
      size: '32"',
      qty: 3,
      priceEur: 629,
    },
    {
      id: 'samsung-s43fm700',
      name: 'Samsung Smart Monitor S43FM700UU',
      spec: '43" 3840x2160 · VA · smart monitor',
      size: '43"',
      qty: 3,
      priceEur: 379,
    },
    {
      id: 'iiyama-g4380uhsu',
      name: 'iiyama G-Master G4380UHSU-B2 Red Eagle',
      spec: '42.5" 3840x2160 · 144 Hz · VA',
      size: '43"',
      qty: 3,
      priceEur: 610,
    },
  ],
  tripleTv: [
    {
      id: 'tcl-50t69c',
      name: 'TCL 50T69C',
      spec: '50" 3840x2160 · QLED',
      size: '50"',
      qty: 3,
      priceEur: 329,
    },
    {
      id: 'hisense-50e7s-pro',
      name: 'Hisense 50E7S PRO',
      spec: '50" 3840x2160 · 144 Hz · Hi-QLED',
      size: '50"',
      qty: 3,
      priceEur: 509,
    },
    {
      id: 'philips-55pml9008',
      name: 'Philips The Xtra 55PML9008/12',
      spec: '55" 3840x2160 · 120 Hz · Mini LED',
      size: '55"',
      qty: 3,
      priceEur: 748,
    },
    {
      id: 'lg-oled55c5',
      name: 'LG OLED55C5 evo',
      spec: '55" 3840x2160 · 120 Hz · OLED',
      size: '55"',
      qty: 3,
      priceEur: 899,
    },
    {
      id: 'hisense-58a6s',
      name: 'Hisense 58A6S',
      spec: '58" 3840x2160 · LED',
      size: '58-60"',
      qty: 3,
      priceEur: 439,
    },
    {
      id: 'hisense-58e7s',
      name: 'Hisense 58E7S Hi-QLED',
      spec: '58" 3840x2160 · Hi-QLED',
      size: '58-60"',
      qty: 3,
      priceEur: 429,
    },
  ],
}

// --- 3. PCs and keyboard -----------------------------------------------------

export const PC_OPTIONS = [
  {
    id: 'pc-essential',
    tier: 'Essential',
    name: 'MSI MAG Infinite S AI 2NVM5-003AT',
    priceEur: 1499,
    blurb: 'Runs a single ultrawide or triples at 1440p with the eye candy turned down.',
    specs: ['Intel Core Ultra 5 225F', 'GeForce RTX 5060 Ti 16 GB', '16 GB DDR5', '1 TB NVMe SSD', 'Windows 11 Home'],
  },
  {
    id: 'pc-performance',
    tier: 'Performance',
    name: 'MSI MPG Infinite Z3 X3D 9NVRR7-882AT',
    priceEur: 2499,
    blurb: 'The sweet spot. The 9800X3D is the fastest sim-racing CPU there is, and the 5070 Ti holds triples at high refresh.',
    specs: ['AMD Ryzen 7 9800X3D', 'GeForce RTX 5070 Ti 16 GB', '32 GB DDR5', '1 TB NVMe SSD', 'Windows 11 Home'],
    recommended: true,
  },
  {
    id: 'pc-ultimate',
    tier: 'Ultimate',
    name: 'MSI MPG Infinite Z3 X3D 9NVVR7-608DE',
    priceEur: 3299,
    blurb: 'Triple 4K, VR, or 60"+ TVs with everything on. Nothing here is the bottleneck.',
    specs: ['AMD Ryzen 7 9800X3D', 'GeForce RTX 5080 16 GB', '64 GB DDR5', '2 TB NVMe SSD', 'Windows 11 Home'],
  },
]

export const KEYBOARD_OPTIONS = [
  {
    id: 'logitech-k400-plus',
    name: 'Logitech Wireless Touch Keyboard K400 Plus',
    spec: 'Wireless keyboard with built-in trackpad — pairs with the Keyboard & Mouse Tray',
    priceEur: 40.99,
  },
]

// --- 4. Third-party price conversion ----------------------------------------

// The third-party list prices above are EUR retail. The VRS offers carry their
// own region currency, so the configurator converts the EUR figures into it with
// this static table. Indicative only — the UI says so next to every converted
// price and in the total.
export const THIRD_PARTY_FX = {
  EUR: 1,
  USD: 1.1,
  AUD: 1.75,
}

// Shown next to every converted price and in the total note.
export const THIRD_PARTY_SOURCE = 'alternate.de, August 2026'

/**
 * Convert an EUR list price into `currency` cents, rounded to a whole unit for
 * anything that is not already EUR (a converted estimate should not pretend to
 * cent precision).
 * @param {number} priceEur
 * @param {string} currency
 * @returns {number} price in minor units of `currency`
 */
export function thirdPartyCents(priceEur, currency) {
  const rate = THIRD_PARTY_FX[currency]
  if (!rate) return Math.round(priceEur * 100)
  if (rate === 1) return Math.round(priceEur * 100)
  return Math.round(priceEur * rate) * 100
}

// --- 5. Compatibility ---------------------------------------------------------

// Wheel bases come in three incompatible families. The offers payload names each
// uDFP20 torque tier differently ('… Wheel Base', '… 9Nm', '… 12Nm'), so a plain
// id match against pairsWith would wrongly reject the 6/12/15Nm tiers — compare
// FAMILIES, not ids.
export const baseFamily = (itemType) => {
  if (!itemType) return null
  if (/^WHEEL_BASE_UPGRADABLE_TORQUE_/.test(itemType)) return 'udfp20'
  if (itemType === 'WHEEL_BASE_UPGRADED_TORQUE_20') return 'dfp20'
  if (itemType === 'DFP15_WHEEL_BASE') return 'dfp15'
  return null
}

export const BASE_FAMILY_LABEL = {
  udfp20: 'the uDFP20 wheel base',
  dfp20: 'the DFP20 20Nm wheel base',
  dfp15: 'the DFP15 wheel base',
}

// Which wheel-base families each base-specific accessory fits. An accessory NOT
// listed here fits every base.
//
// The QR Connect WHEEL Adapter is deliberately absent: it is the wheel-side half
// of the quick release and works with every wheel base we sell. The QR Connect
// (u)DFP20 Adapter is the base-side counterpart and is DFP20/uDFP20 only, so
// choosing the DFP15 marks it not compatible. Same for the Hub Adapter and the
// two quick-release halves.
export const BASE_SPECIFIC_ACCESSORIES = {
  R295_ADAPTER: ['udfp20', 'dfp20'],
  MOUNTING_BRACKET: ['udfp20', 'dfp20'],
  HUB_ADAPTER_V2: ['udfp20', 'dfp20'],
  QUICK_RELEASE_BASE_SIDE_80MM: ['udfp20', 'dfp20'],
  QUICK_RELEASE_WHEEL_SIDE: ['udfp20', 'dfp20'],
}

// The backend's `optionalFor` on these two is wrong for a configurator and must
// not gate them:
//   - QR_CONNECT_WHEEL_ADAPTER says it is optional-for the DFP15, but it fits
//     every base.
//   - R295_ADAPTER says it is optional-for the R295 steering wheel. There is no
//     such relationship: which base you picked decides whether it fits, not which
//     wheel. BASE_SPECIFIC_ACCESSORIES above is the only rule that governs it.
export const IGNORE_OPTIONAL_FOR = new Set(['QR_CONNECT_WHEEL_ADAPTER', 'R295_ADAPTER'])

// --- 6. Assembly and shipping -------------------------------------------------

// PLACEHOLDER PRICING — the fully-assembled figure is the EUR 600 placeholder we
// agreed, not a quoted rate.
export const ASSEMBLY_OPTIONS = [
  {
    id: 'assembly-self',
    name: 'I will assemble it myself',
    spec: 'We ship the parts flat-packed with the manuals. A GT-2 build takes about two hours with two people.',
    priceEur: 0,
  },
  {
    id: 'assembly-full',
    name: 'Fully assembled',
    spec: 'Built, cabled, calibrated and test-driven here, then crated. You unbox it and drive.',
    priceEur: 600,
    placeholder: true,
  },
]

// PLACEHOLDER PRICING — a rig ships as freight and the real rate depends on the
// destination. These are stand-ins so the total is not misleadingly low.
export const SHIPPING_OPTIONS = [
  {
    id: 'ship-standard',
    name: 'Standard freight',
    spec: 'Kerbside delivery, tracked. The usual option for a full rig.',
    priceEur: 149,
    placeholder: true,
  },
  {
    id: 'ship-express',
    name: 'Express freight',
    spec: 'Prioritised handling and a shorter transit window.',
    priceEur: 249,
    placeholder: true,
  },
  {
    id: 'ship-pickup',
    name: 'I will arrange collection',
    spec: 'Your own forwarder collects from our warehouse.',
    priceEur: 0,
  },
]

// --- 7. VRS Academy subscription ----------------------------------------------

// The three plans come LIVE from the region surface bundle (getSubscriptions);
// only the ordering and the per-plan blurb live here.
export const SUBSCRIPTION_ORDER = ['casual', 'dedicated', 'competitive']

export const SUBSCRIPTION_BLURB = {
  casual: 'Your own telemetry, stats and analysis tools. Free, forever.',
  dedicated: 'Unlimited access to one data pack plus your teammates’ telemetry.',
  competitive: 'Every essential data pack, competitive analysis and the full VRS toolset.',
}

// --- 8. Guided flow ("new to sim racing") -------------------------------------

// Budget ceilings are stated in EUR and converted with THIRD_PARTY_FX, the same
// table the third-party prices use.
export const BUDGET_BANDS = [
  { id: 'starter', minEur: 0, maxEur: 4000, blurb: 'A real direct-drive rig with a single screen.' },
  { id: 'mid', minEur: 4000, maxEur: 7000, blurb: 'Stronger base, three-pedal set, triple screens.' },
  { id: 'high', minEur: 7000, maxEur: 11000, blurb: 'Near the top of the range, built and delivered ready.' },
  { id: 'max', minEur: 11000, maxEur: 25000, blurb: 'Everything we make, at its best specification.' },
]

// How the rider wants to see the track. `sizes` maps an inch choice to the mount
// that carries it, which is what makes the screen step downstream self-scoping.
export const SCREEN_LAYOUTS = [
  {
    id: 'single',
    title: 'One ultrawide',
    blurb: 'A single super-ultrawide, 49" or 57". Simplest to set up, one cable, no bezels.',
    previewType: 'SX57_SINGLE_MONITOR_MOUNT_100x100',
    sizes: [
      { size: '49"', mount: 'SX57_SINGLE_MONITOR_MOUNT_100x100', freeStanding: false },
      { size: '57"', mount: 'SX57_SINGLE_MONITOR_MOUNT_100x100', freeStanding: false },
    ],
  },
  {
    id: 'triple',
    title: 'Three monitors',
    blurb: 'Real peripheral vision. You see the apex and the car alongside you.',
    previewType: 'TX32_TRIPLE_MONITOR_STAND_100x100',
    sizes: [
      { size: '27"', mount: 'TX32_TRIPLE_MONITOR_STAND_100x100', freeStanding: false },
      { size: '32"', mount: 'TX32_TRIPLE_MONITOR_STAND_100x100', freeStanding: false },
      { size: '43"', mount: 'TX43_TRIPLE_MONITOR_STAND_100x100', freeStanding: true },
    ],
  },
  {
    id: 'tv',
    title: 'Three TVs, free-standing',
    blurb: 'The biggest picture we support. The stand sits behind the rig instead of bolting to it.',
    previewType: 'TX60_TV_STAND',
    sizes: [
      { size: '50"', mount: 'TX60_TV_STAND', freeStanding: true },
      { size: '55"', mount: 'TX60_TV_STAND', freeStanding: true },
      { size: '58-60"', mount: 'TX60_TV_STAND', freeStanding: true },
    ],
  },
]

// Every additional steering wheel needs its own wheel-side quick release. That is
// the QR Connect Wheel Adapter, which fits all of our wheel bases — so the extra
// -wheel count never depends on which base the solver landed on.
export const EXTRA_WHEEL_ADAPTER = 'QR_CONNECT_WHEEL_ADAPTER'

// Accessories a build can legitimately hold more than one of.
export const QUANTITY_TYPES = new Set(['QR_CONNECT_WHEEL_ADAPTER', 'R295_ADAPTER'])

// A prerequisite that more than one itemType can satisfy. The 3-Pedal Set
// contains the Throttle & Brake, so it satisfies anything that asks for it.
export const REQUIREMENT_ALIASES = {
  PEDALS_THROTTLE_AND_BRAKE: ['PEDALS_THROTTLE_AND_BRAKE', 'PEDALS_THROTTLE_AND_BRAKE_CLUTCH_PEDAL'],
}
